import { PersistStorage } from 'zustand/middleware';

import {
  FilterCategoryField,
  IFilters,
  IPerformanceTileView,
  IProductCenterStore,
  TableTypeEnum,
  TileTabEnum,
} from './product-center.store.model';
import {
  Product,
  View,
} from '../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants } from '../product-center-table-wrapper/product-center-table-wrapper.constants';

export const hasAtLeastOneFilter = (filters: IFilters): boolean =>
  Object.values(filters).some(
    (filterCategory) =>
      filterCategory && filterCategory.some((cat) => cat.isSelected),
  );

export const hasCategoryFieldAtLeastOneFilter = (
  filters: IFilters,
  categoryField: FilterCategoryField | undefined,
): boolean =>
  filters[categoryField ?? '']?.some((cat) => cat.isSelected) || false;

export const sessionStoragePersist: PersistStorage<IProductCenterStore> = {
  getItem: (name) => {
    const value = sessionStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  },
  setItem: (name, value) => {
    sessionStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name) => {
    sessionStorage.removeItem(name);
  },
};

export const filterProducts = (
  productsToFilter: Product[],
  updatedFilters: Record<string, any>,
): Product[] => {
  return productsToFilter.filter((product) =>
    Object.keys(updatedFilters).every((key) => {
      const field = key as FilterCategoryField;
      const filterCategory = updatedFilters[field];
      if (!filterCategory) {
        return true;
      }
      const filterValues =
        filterCategory.filter((cat) => cat.isSelected).map((cat) => cat.name) ||
        [];
      const productFieldValue = product.productName[field] ?? '';
      return filterValues.length > 0
        ? filterValues.includes(productFieldValue)
        : true;
    }),
  );
};

export const mapProductCodes = (
  products: Product[],
): { productCode: string; parentProductCode: string | null }[] => {
  return products.map((product: Product) => ({
    productCode: product.productCode,
    parentProductCode: product.parentProductCode,
  }));
};

export const getSelectedProductsField = (
  selectedView: View,
  selectedTabInView: number,
): TableTypeEnum | TileTabEnum => {
  if (selectedView === View.TABLE) {
    switch (selectedTabInView) {
      case Constants.TABLE_TAB_INDEX.PERFORMANCE:
        return TableTypeEnum.PERFORMANCE;
      case Constants.TABLE_TAB_INDEX.PRICE:
        return TableTypeEnum.PRICE;
      default:
        return TableTypeEnum.PERFORMANCE;
    }
  } else {
    switch (selectedTabInView) {
      case Constants.TILES_TAB_INDEX.ONE_YEAR:
        return TileTabEnum.ONE_YEAR;
      case Constants.TILES_TAB_INDEX.THREE_YEARS:
        return TileTabEnum.THREE_YEARS;
      case Constants.TILES_TAB_INDEX.FIVE_YEARS:
        return TileTabEnum.FIVE_YEARS;
      case Constants.TILES_TAB_INDEX.TEN_YEARS:
        return TileTabEnum.TEN_YEARS;
      case Constants.TILES_TAB_INDEX.SINCE_INCEPTION:
        return TileTabEnum.SINCE_INCEPTION;
      default:
        return TileTabEnum.ONE_YEAR;
    }
  }
};

export const isProductChartEmpty = (
  product: Product,
  performanceTileView: IPerformanceTileView,
) => {
  const data =
    product.cumulativeChart?.data?.[performanceTileView.currentFundReturnKey];
  return !data || data.length === 0;
};

export const resetFilterCounts = (
  fields: FilterCategoryField[],
  updatedFilters: IFilters,
) => {
  fields.forEach((field) => {
    updatedFilters[field] =
      updatedFilters[field]?.map((cat) => ({
        ...cat,
        count: 0,
      })) || [];
  });
};
